import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { ScrollView, Pressable } from "react-native";
import { PressableScale } from "pressto";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { View, Text, FormInput, Switch, Card } from "@src/components";
import { Icon } from "@src/components/Icon";
import { UniversalDatePicker } from "@src/components/UniversalDatePicker";
import { TimePickerWith24Hour } from "@src/components/TimePickerWith24Hour";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { formatTime12 } from "@src/utils/format-date";
import { formatTime24 } from "@src/features/alarms/utils/format-time";
import { makeDateTimeRecord } from "@visioni/schedules";
import { isExceptionAllDayRange } from "@src/features/schedules/utils/schedule-utils";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { isWeb } from "@src/utils/web-helpers";
import { buildAlarmSettingsRoutes } from "@src/features/alarms/utils/alarm-settings-routes";
import { pendingExceptionDetailUpdate, type ExceptionParam } from "./exception-shared";
import type { AlarmAction } from "@src/services/api/types";
import Animated, {
  FadeOut,
  FadeIn,
  LinearTransition,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
  interpolateColor,
} from "react-native-reanimated";

export default function EditExceptionDetailsScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const { theme } = useUnistyles();
  const { headerLeft } = useHeaderBackButton();

  const {
    tenantId,
    siteId,
    exception: exceptionParam,
    isNew,
    allCameraKeys: allCameraKeysParam,
  } = useLocalSearchParams<{
    tenantId: string;
    siteId: string;
    exception: string;
    isNew?: string;
    allCameraKeys?: string;
  }>();

  const routes = useMemo(() => buildAlarmSettingsRoutes(tenantId, siteId), [tenantId, siteId]);

  const initialData = useMemo<ExceptionParam | null>(() => {
    if (!exceptionParam) return null;
    try {
      return JSON.parse(exceptionParam) as ExceptionParam;
    } catch {
      return null;
    }
  }, [exceptionParam]);

  const [reason, setReason] = useState("");
  const [action, setAction] = useState<AlarmAction>("armed");
  const [initialDateIso] = useState(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0)).toISOString();
  });
  const [startDate, setStartDate] = useState(initialDateIso);
  const [endDate, setEndDate] = useState(initialDateIso);
  const [startHour, setStartHour] = useState(0);
  const [startMinute, setStartMinute] = useState(0);
  const [endHour, setEndHour] = useState(0);
  const [endMinute, setEndMinute] = useState(0);
  const [endIs24Hour, setEndIs24Hour] = useState(false);
  const [isAllDay, setIsAllDay] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const isEndBeforeStart = useMemo(() => {
    const startDatePart = startDate.split("T")[0];
    const endDatePart = endDate.split("T")[0];

    if (isAllDay) return endDatePart < startDatePart;

    if (endDatePart < startDatePart) return true;
    if (endDatePart > startDatePart) return false;

    const finalEnd = endIs24Hour ? 24 : endHour;
    return finalEnd * 60 + endMinute < startHour * 60 + startMinute;
  }, [startDate, endDate, startHour, startMinute, endHour, endMinute, endIs24Hour, isAllDay]);

  useEffect(() => {
    pendingExceptionDetailUpdate.current = null;
  }, []);

  const actionProgress = useDerivedValue(() => withTiming(action === "armed" ? 0 : 1, { duration: 300 }));

  const activeBoxAnimatedStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      actionProgress.value,
      [0, 1],
      [
        theme.colors.alarmStateBadges.armed.background as string,
        theme.colors.alarmStateBadges.disarmed.background as string,
      ],
    ),
    borderColor: interpolateColor(
      actionProgress.value,
      [0, 1],
      [theme.colors.alarmStateBadges.armed.border as string, theme.colors.alarmStateBadges.disarmed.border as string],
    ),
  }));

  useEffect(() => {
    if (!initialData) return;

    setReason(initialData.reason);
    setAction(initialData.action);

    const { start, end } = initialData.period;

    if (start.year && start.month && start.day) {
      const d = new Date(Date.UTC(start.year, start.month - 1, start.day, 12, 0, 0));
      setStartDate(d.toISOString());
    }
    setStartHour(start.hour === 24 ? 0 : (start.hour ?? 0));
    setStartMinute(start.minute ?? 0);

    if (end.year && end.month && end.day) {
      const d = new Date(Date.UTC(end.year, end.month - 1, end.day, 12, 0, 0));
      setEndDate(d.toISOString());
    }
    const is24 = end.hour === 24;
    setEndIs24Hour(is24);
    setEndHour(is24 ? 0 : (end.hour ?? 0));
    setEndMinute(end.minute ?? 0);

    if (isExceptionAllDayRange(start, end)) {
      setIsAllDay(true);
    }
  }, [initialData]);

  const canSave = reason && !isEndBeforeStart;

  const handleDone = useCallback(() => {
    if (!reason || isEndBeforeStart) return;

    const finalEndHour = endIs24Hour ? 24 : endHour;
    const startDatePart = startDate.split("T")[0];
    const [sY, sM, sD] = startDatePart.split("-").map(Number);
    const endDatePart = endDate.split("T")[0];
    const [eY, eM, eD] = endDatePart.split("-").map(Number);

    const exceptionData = {
      reason,
      action,
      period: {
        start: makeDateTimeRecord(sY, sM, sD, isAllDay ? 0 : startHour, isAllDay ? 0 : startMinute),
        end: makeDateTimeRecord(eY, eM, eD, isAllDay ? 24 : finalEndHour, isAllDay ? 0 : endMinute),
      },
    };

    if (isNew === "true" && allCameraKeysParam) {
      router.replace({
        pathname: routes.editException as never,
        params: {
          exception: JSON.stringify(exceptionData),
          includedCameraKeys: JSON.stringify([]),
          timezone: "UTC",
          isNew: "true",
        },
      });
    } else {
      pendingExceptionDetailUpdate.current = exceptionData;
      router.back();
    }
  }, [
    reason,
    action,
    startDate,
    endDate,
    startHour,
    startMinute,
    endHour,
    endMinute,
    endIs24Hour,
    isAllDay,
    isEndBeforeStart,
    isNew,
    allCameraKeysParam,
    router,
    routes,
  ]);

  const formatTimeDisplay = (hour: number, minute: number, is24Hour = false): string => {
    if (is24Hour && hour === 0 && minute === 0) return "24:00";
    return is24Hour ? formatTime24(hour, minute) : formatTime12(hour, minute);
  };

  const headerTitle = useMemo(
    () => (
      <View flexDirection="row" justifyContent="space-between" alignItems="center" flex={1}>
        <View flex={1}>
          <Text textStyle="md/semibold" color="text" textAlign="center" style={styles.headerTitle}>
            {reason || (isNew === "true" ? "New Exception" : "Edit Exception")}
          </Text>
        </View>
        <PressableScale
          onPress={handleDone}
          style={[
            styles.doneButton,
            {
              opacity: canSave ? 1 : 0.5,
            },
          ]}
          enabled={Boolean(canSave)}
        >
          <Text textStyle="md/medium" color={canSave ? "text" : "textMuted"}>
            Done
          </Text>
        </PressableScale>
      </View>
    ),
    [handleDone, canSave, reason, isNew],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
      headerTitle: () => headerTitle,
    });
  }, [navigation, headerLeft, headerTitle]);

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.colors.background }}
      contentContainerStyle={{
        paddingBottom: theme.spacing["10xl"],
        paddingTop: theme.spacing["7xl"],
        paddingHorizontal: theme.spacing["5xl"],
        gap: theme.spacing["2xl"],
      }}
      keyboardDismissMode="on-drag"
    >
      {isWeb && (
        <View alignItems="flex-end" mb="xl">
          <Pressable onPress={handleDone} style={{ opacity: canSave ? 1 : 0.5 }} disabled={!canSave}>
            <Text textStyle="md/semibold" color={canSave ? "navy" : "textMuted"}>
              Done
            </Text>
          </Pressable>
        </View>
      )}
      <Card style={styles.card}>
        <View mb="lg">
          <Text textStyle="sm/regular" mb="lg">
            Name
          </Text>
          <FormInput
            value={reason}
            onChangeText={setReason}
            placeholder="Enter exception name"
            autoCapitalize="words"
            containerStyle={{
              backgroundColor: theme.colors.surfaceCard,
              borderColor: theme.colors.interactive.buttons.default_background,
            }}
          />
        </View>

        <View mt="xl" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text textStyle="sm/regular">Action</Text>
          <SegmentedSelector
            optionsLabels={[
              { title: "Arm", icon: "lockClosed" },
              { title: "Disarm", icon: "lockOpen" },
            ]}
            selectedOption={action === "armed" ? "Arm" : "Disarm"}
            onSelectOption={(option) => setAction(option === "Arm" ? "armed" : "disarmed")}
            width={isWeb ? 220 : 180}
            activeBoxStyle={activeBoxAnimatedStyle}
            containerStyle={styles.container}
          />
        </View>
      </Card>

      <Animated.View layout={LinearTransition} style={[styles.card, styles.animatedCard]}>
        <View mt="xl">
          <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="2xl">
            <Text textStyle="sm/regular">Starts</Text>

            <View flexDirection="row" alignItems="center" gap="md">
              <Animated.View layout={LinearTransition}>
                <UniversalDatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="Select start date"
                  size="medium"
                  textColor={theme.colors.text}
                  textInputPaddingLeft={0}
                  dateFormat="dd-MMM-yyyy"
                  containerFormInputStyle={{
                    paddingHorizontal: 0,
                    paddingLeft: theme.spacing.lg,
                    width: 120,
                    borderColor: theme.colors.interactive.buttons.default_background,
                  }}
                  showIcon={false}
                />
              </Animated.View>

              {!isAllDay && (
                <Animated.View entering={FadeIn} exiting={FadeOut}>
                  <PressableScale onPress={() => setShowStartTimePicker(true)}>
                    <View
                      bg="surface"
                      borderWidth={1}
                      py="md"
                      px="lg"
                      rounded={8}
                      flexDirection="row"
                      alignItems="center"
                      width={90}
                      justifyContent="center"
                      style={{ borderColor: theme.colors.interactive.buttons.default_background }}
                    >
                      <Text textStyle="md/regular" color="text" textAlign="center">
                        {formatTimeDisplay(startHour, startMinute)}
                      </Text>
                    </View>
                  </PressableScale>
                </Animated.View>
              )}
            </View>
          </View>
        </View>

        <Animated.View layout={LinearTransition}>
          <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="2xl">
            <Text textStyle="sm/regular">Ends</Text>

            <View flexDirection="row" alignItems="center" gap="md">
              <Animated.View layout={LinearTransition}>
                <UniversalDatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholder="Select end date"
                  size="medium"
                  textColor={theme.colors.text}
                  textInputPaddingLeft={0}
                  dateFormat="dd-MMM-yyyy"
                  containerFormInputStyle={{
                    paddingHorizontal: 0,
                    paddingLeft: theme.spacing.lg,
                    width: 120,
                    borderColor: theme.colors.interactive.buttons.default_background,
                  }}
                  showIcon={false}
                />
              </Animated.View>

              {!isAllDay && (
                <Animated.View entering={FadeIn} exiting={FadeOut}>
                  <PressableScale onPress={() => setShowEndTimePicker(true)}>
                    <View
                      bg="surface"
                      borderWidth={1}
                      style={{ borderColor: theme.colors.interactive.buttons.default_background }}
                      py="md"
                      px="lg"
                      rounded={8}
                      flexDirection="row"
                      alignItems="center"
                      width={90}
                      justifyContent="center"
                    >
                      <Text textStyle="md/regular" color="text" textAlign="center">
                        {formatTimeDisplay(endHour, endMinute, endIs24Hour)}
                      </Text>
                    </View>
                  </PressableScale>
                </Animated.View>
              )}
            </View>
          </View>
        </Animated.View>

        <Animated.View layout={LinearTransition}>
          <View py="xl" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Text textStyle="sm/regular">All day</Text>
            <Switch active={isAllDay} onToggle={() => setIsAllDay(!isAllDay)} size="large" />
          </View>
        </Animated.View>

        {isEndBeforeStart && (
          <Animated.View entering={FadeIn} exiting={FadeOut} layout={LinearTransition}>
            <Text textStyle="xs/regular" color="danger">
              End date must be after start date
            </Text>
          </Animated.View>
        )}

        <TimePickerWith24Hour
          isVisible={showStartTimePicker}
          hour={startHour}
          minute={startMinute}
          onConfirm={(hour, minute) => {
            setStartHour(hour);
            setStartMinute(minute);
            setShowStartTimePicker(false);
          }}
          onCancel={() => setShowStartTimePicker(false)}
        />
        <TimePickerWith24Hour
          isVisible={showEndTimePicker}
          hour={endHour}
          minute={endMinute}
          initialHour24={endIs24Hour}
          onConfirm={(hour, minute) => {
            if (hour === 24) {
              setEndIs24Hour(true);
              setEndHour(0);
            } else {
              setEndIs24Hour(false);
              setEndHour(hour);
            }
            setEndMinute(minute);
            setShowEndTimePicker(false);
          }}
          onCancel={() => setShowEndTimePicker(false)}
        />
      </Animated.View>
    </ScrollView>
  );
}

const styles = StyleSheet.create((theme) => ({
  doneButton: {
    paddingHorizontal: theme.spacing["5xl"],
    borderRadius: theme.spacing["6xl"],
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    marginRight: theme.spacing["5xl"],
    borderCurve: "continuous",
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitle: {
    marginLeft: theme.spacing["5xl"],
  },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderColor: theme.colors.borderCard,
  },
  container: {
    height: 40,
  },
  animatedCard: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    padding: theme.spacing["4xl"],
  },
}));
